import { Scale } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ComparisonTable from '../components/ComparisonTable';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import { tx } from '../i18n';
import UnlockPanel from '../components/UnlockPanel';
import { useComparison } from '../hooks/ComparisonContext';
import type { ComparisonRow } from '../types/api';

/** Up to three homes free, more once a bundle is bought. */
export default function ComparePage() {
  const { comparison, loading, error, remove, refresh } = useComparison();
  const [unlockRow, setUnlockRow] = useState<ComparisonRow | null>(null);
  const unlockRef = useRef<HTMLDivElement>(null);

  // Bring the unlock options into view when a home's "Unlock scores" is pressed.
  useEffect(() => {
    if (unlockRow) {
      unlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [unlockRow]);

  if (loading && !comparison) {
    return (
      <div className="page">
        <div className="card loading-panel">
          <Spinner label={tx('Loading your comparison', 'Karşılaştırmanız yükleniyor',
    'Wczytywanie porównania',
    'A carregar a sua comparação',
    'Cargando tu comparación')} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page page--narrow">
        <Notice tone="error" title={tx('We could not load your comparison', 'Karşılaştırmanız yüklenemedi',
    'Nie udało się wczytać porównania',
    'Não foi possível carregar a sua comparação',
    'No se ha podido cargar tu comparación')} onRetry={refresh}>
          {error}
        </Notice>
      </div>
    );
  }

  if (!comparison || comparison.rows.length === 0) {
    return (
      <div className="page page--narrow stack">
        <section className="card stack">
          <div className="stack stack--tight">
            <span className="category__icon">
              <Scale size={18} aria-hidden="true" />
            </span>
            <h1 className="card__title">{tx('Nothing to compare yet', 'Henüz karşılaştırılacak konut yok',
    'Nie ma jeszcze czego porównywać',
    'Ainda não há nada para comparar',
    'Todavía no hay nada que comparar')}</h1>
            <p className="muted">
              {comparison?.paymentsRequired === false
                ? tx(
                    'Compare homes side by side: every score, the regional ranking and running costs.',
                    'Konutları yan yana karşılaştırın: tüm puanlar, bölge sıralaması ve giderler.',
    'Porównuj mieszkania obok siebie: wszystkie oceny, ranking regionalny i koszty eksploatacji.',
    'Compare casas lado a lado: todas as pontuações, o ranking regional e os custos de utilização.',
    'Compara viviendas una al lado de otra: todas las puntuaciones, el ranking regional y los costes.',
                  )
                : tx(
                    `Compare up to ${comparison?.freeLimit ?? 3} homes side by side, free. Unlocked homes never use a free slot.`,
                    `En fazla ${comparison?.freeLimit ?? 3} konutu ücretsiz karşılaştırın. Kilidi açılmış konutlar ücretsiz hakkınızdan düşmez.`,
    `Porównaj bezpłatnie do ${comparison?.freeLimit ?? 3} mieszkań obok siebie. Odblokowane mieszkania nigdy nie zajmują bezpłatnego miejsca.`,
    `Compare gratuitamente até ${comparison?.freeLimit ?? 3} casas lado a lado. As casas desbloqueadas nunca ocupam um lugar gratuito.`,
    `Compara gratis hasta ${comparison?.freeLimit ?? 3} viviendas. Las viviendas desbloqueadas nunca ocupan una plaza gratuita.`,
                  )}
            </p>
          </div>

          <ol className="how-compare">
            <li>
              <span className="step__number">1</span>
              <span>{tx('Search a postcode from the listing.', 'İlandaki posta kodunu arayın.',
    'Wyszukaj kod pocztowy z ogłoszenia.',
    'Pesquise um código postal do anúncio.',
    'Busca un código postal del anuncio.')}</span>
            </li>
            <li>
              <span className="step__number">2</span>
              <span>
                {tx('Press ', 'Düşündüğünüz her konutun yanındaki ',
    'Naciśnij ',
    'Carregue em ',
    'Pulsa ')}
                <strong>{tx('Compare', 'Karşılaştır',
    'Porównaj',
    'Comparar',
    'Comparar')}</strong>
                {tx(
                  ' next to each home you are considering. You can add homes from different postcodes.',
                  ' düğmesine basın. Farklı posta kodlarından konut ekleyebilirsiniz.',
    ' przy każdym rozważanym mieszkaniu. Możesz dodawać mieszkania z różnych kodów pocztowych.',
    ' junto a cada casa que está a considerar. Pode juntar casas de códigos postais diferentes.',
    ' junto a cada vivienda que estés considerando. Puedes añadir viviendas de distintos códigos postales.',
                )}
              </span>
            </li>
            <li>
              <span className="step__number">3</span>
              <span>
                {tx('Your list collects at the bottom of the screen. Press ', 'Listeniz ekranın altında birikir. Burada görmek için ',
    'Twoja lista zbiera się na dole ekranu. Naciśnij ',
    'A sua lista junta-se no fundo do ecrã. Carregue em ',
    'Tu lista se acumula abajo en la pantalla. Pulsa ')}
                <strong>{tx('Compare homes', 'Konutları karşılaştır',
    'Porównaj mieszkania',
    'Comparar casas',
    'Comparar viviendas')}</strong>
                {tx(' to see them here.', ' düğmesine basın.',
    ', aby zobaczyć je tutaj.',
    ' para as ver aqui.',
    ' para verlas aquí.')}
              </span>
            </li>
          </ol>

          <Link to="/" className="button" style={{ alignSelf: 'flex-start' }}>
            {tx('Search a postcode', 'Posta kodu ara',
    'Wyszukaj kod pocztowy',
    'Pesquisar um código postal',
    'Buscar un código postal')}
          </Link>
        </section>
      </div>
    );
  }

  const lockedCount = comparison.rows.filter((row) => !row.detailed).length;
  const selectedForUnlock =
    unlockRow && comparison.rows.some((row) => row.propertyAddressId === unlockRow.propertyAddressId)
      ? unlockRow
      : null;

  return (
    <div className="page stack">
      <div className="row row--between">
        <div>
          <h1 className="verdict__title">{tx('Comparison', 'Karşılaştırma',
    'Porównanie',
    'Comparação',
    'Comparación')}</h1>
          <p className="small muted">
            {comparison.rows.length === 1
              ? tx(
                  '1 home · nothing to compare it with yet',
                  '1 konut · henüz kıyaslanacak bir şey yok',
                  '1 mieszkanie · nie ma go jeszcze z czym porównać',
                  '1 casa · ainda sem nada com que a comparar',
                  '1 vivienda · todavía sin nada con que compararla',
                )
              : tx(
              `${comparison.rows.length} home${comparison.rows.length === 1 ? '' : 's'}`,
              `${comparison.rows.length} konut`,
    `${comparison.rows.length} ${comparison.rows.length === 1 ? 'mieszkanie' : 'mieszkań'}`,
    `${comparison.rows.length} casa${comparison.rows.length === 1 ? '' : 's'}`,
    `${comparison.rows.length} vivienda${comparison.rows.length === 1 ? '' : 's'}`,
            )}
            {comparison.paymentsRequired
              ? tx(
                  ` · ${comparison.usedFreeSlots}/${comparison.freeLimit} free slots used`,
                  ` · ${comparison.usedFreeSlots}/${comparison.freeLimit} ücretsiz hak kullanıldı`,
    ` · wykorzystano ${comparison.usedFreeSlots}/${comparison.freeLimit} bezpłatnych miejsc`,
    ` · ${comparison.usedFreeSlots}/${comparison.freeLimit} lugares gratuitos usados`,
    ` · ${comparison.usedFreeSlots}/${comparison.freeLimit} plazas gratuitas usadas`,
                )
              : ''}
            {comparison.paymentsRequired && comparison.bundleSlotsLeft > 0
              ? tx(
                  ` · ${comparison.bundleSlotsLeft} bundle unlock(s) left`,
                  ` · paketinizde ${comparison.bundleSlotsLeft} hak kaldı`,
    ` · pozostało ${comparison.bundleSlotsLeft} odblokowań z pakietu`,
    ` · restam ${comparison.bundleSlotsLeft} desbloqueios do pacote`,
    ` · quedan ${comparison.bundleSlotsLeft} desbloqueos del pack`,
                )
              : ''}
          </p>
        </div>
        <Link to="/" className="button button--secondary button--small">
          {tx('Add another home', 'Başka konut ekle',
    'Dodaj kolejne mieszkanie',
    'Juntar outra casa',
    'Añadir otra vivienda')}
        </Link>
      </div>

      {lockedCount > 0 ? (
        <p className="small muted">
          {tx(
            "The free comparison shows each home's overall score and a sentence per category. Category scores, regional rankings and running costs unlock per home.",
            'Ücretsiz karşılaştırma her konutun genel puanını ve kategori başına bir cümleyi gösterir. Kategori puanları, bölge sıralaması ve giderler konut başına açılır.',
    'Bezpłatne porównanie pokazuje ocenę ogólną każdego mieszkania i jedno zdanie na kategorię. Oceny kategorii, rankingi regionalne i koszty eksploatacji odblokowuje się osobno dla każdego mieszkania.',
    'A comparação gratuita mostra a pontuação global de cada casa e uma frase por categoria. As pontuações por categoria, os rankings regionais e os custos desbloqueiam-se casa a casa.',
    'La comparación gratuita muestra la puntuación global de cada vivienda y una frase por categoría. Las puntuaciones por categoría, los rankings regionales y los costes se desbloquean vivienda a vivienda.',
          )}
        </p>
      ) : null}

      <ComparisonTable
        comparison={comparison}
        onRemove={(propertyAddressId) => {
          void remove(propertyAddressId);
        }}
        onUnlock={setUnlockRow}
      />

      {selectedForUnlock ? (
        <div className="stack stack--tight" ref={unlockRef}>
          <div className="row row--between">
            <h2 className="card__title">
              {tx('Unlock', 'Kilidi aç:',
    'Odblokuj',
    'Desbloquear',
    'Desbloquear')} {selectedForUnlock.addressLine}
            </h2>
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => setUnlockRow(null)}
            >
              {tx('Close', 'Kapat',
    'Zamknij',
    'Fechar',
    'Cerrar')}
            </button>
          </div>
          <UnlockPanel
            options={comparison.unlockOptions}
            propertyAddressId={selectedForUnlock.propertyAddressId}
            addressLine={selectedForUnlock.addressLine}
            bundleSlotsLeft={comparison.bundleSlotsLeft}
          />
        </div>
      ) : null}
    </div>
  );
}
